import { emptyCondition } from '@activepieces/shared';
import { t } from 'i18next';
import { Sparkle } from 'lucide-react';
import React from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';

import { useBuilderStateContext } from '../../builder-hooks';

import { BranchConditionGroup } from '@/app/builder/step-settings/branch-settings/branch-condition-group';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type BranchSettingsProps = {
  readonly: boolean;
  branchIndex: number;
};

const BranchSettings = React.memo(
  ({ readonly, branchIndex }: BranchSettingsProps) => {
    const form = useFormContext();
    const { fields, append, remove, update } = useFieldArray({
      control: form.control,
      name: `settings.branches.${branchIndex}.conditions`,
    });

    const handleDelete = (groupIndex: number, conditionIndex: number) => {
      const conditions = form.getValues(
        `settings.branches.${branchIndex}.conditions`,
      );
      const newConditionsGroup = [...conditions[groupIndex]];
      const isSingleGroup = conditions.length === 1;
      const isSingleConditionInGroup = newConditionsGroup.length === 1;

      if (isSingleGroup && isSingleConditionInGroup) {
        update(groupIndex, [emptyCondition]);
      } else if (isSingleConditionInGroup) {
        remove(groupIndex);
      } else {
        newConditionsGroup.splice(conditionIndex, 1);
        update(groupIndex, newConditionsGroup);
      }
    };

    const handleAnd = (groupIndex: number) => {
      const conditions = form.getValues(
        `settings.branches.${branchIndex}.conditions`,
      );
      conditions[groupIndex] = [...conditions[groupIndex], emptyCondition];
      update(groupIndex, conditions[groupIndex]);
    };

    const handleOr = () => {
      append([[emptyCondition]]);
    };

    const { requestAiHelp, selectedStep } = useBuilderStateContext((state) => ({
      requestAiHelp: state.requestAiHelp,
      selectedStep: state.selectedStep,
    }));

    return (
      <div className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
        <div className="flex flex-col gap-2 items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="basic"
                className="w-auto"
                onClick={() => requestAiHelp(selectedStep ?? '')}
              >
                <div className="flex gap-2 items-center">
                  <Sparkle className="w-4 h-4" />
                  {t('Need Help ?')}
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t('Open AI Builder')}
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="text-md ">{t('Execute If')}</div>
        {fields.map((fieldGroup, groupIndex) => (
          <BranchConditionGroup
            key={fieldGroup.id}
            readonly={readonly}
            branchIndex={branchIndex}
            numberOfGroups={fields.length}
            groupIndex={groupIndex}
            onAnd={() => handleAnd(groupIndex)}
            onOr={handleOr}
            handleDelete={(conditionIndex: number) =>
              handleDelete(groupIndex, conditionIndex)
            }
          />
        ))}
      </div>
    );
  },
);

BranchSettings.displayName = 'BranchSettings';
export { BranchSettings };
